import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Package,
  FileText,
  Truck,
  FileSignature,
  Download,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  Upload,
  Trash2,
  FileCheck,
  Loader2,
  CheckCircle2,
  Clock,
} from "lucide-react";

export default function AdminClienteDetailPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({
    envios: true,
    rdas: false,
    cedibles: false,
    consolidado: false,
  });
  const [busy, setBusy] = useState(null);
  const [rdaMes, setRdaMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [cedibleMes, setCedibleMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const rdaInputRef = useRef(null);
  const cedibleInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${userId}/summary`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [userId]);

  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));

  const downloadBatchSap = async (b) => {
    setBusy(`batch-${b.id}`);
    try {
      const res = await api.get(`/batches/${b.id}/sap`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAP_${(b.descripcion || "envio").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("SAP descargado");
      load();
    } catch {
      toast.error("Error al descargar SAP");
    } finally {
      setBusy(null);
    }
  };

  const downloadClientSap = async () => {
    setBusy("client-sap");
    try {
      const res = await api.get(
        `/admin/sap-by-client/${userId}/download?only_pending=false`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAP_${(data.user.id_cliente || data.user.razon_social || "cliente").replace(
        /\s+/g,
        "_"
      )}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("SAP consolidado descargado");
      load();
    } catch {
      toast.error("Error al descargar SAP consolidado");
    } finally {
      setBusy(null);
    }
  };

  const downloadConsolidadoMonth = async (monthKey) => {
    setBusy(`consolidado-${monthKey}`);
    try {
      const res = await api.get(
        `/admin/consolidado/${userId}/download?month=${monthKey}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const tag = monthKey.replace("-", "");
      a.download = `Consolidado_SAP_RDA_${(
        data.user.id_cliente || data.user.razon_social || "cliente"
      ).replace(/\s+/g, "_")}_${tag}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Consolidado del mes descargado");
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar");
    } finally {
      setBusy(null);
    }
  };

  const downloadConsolidado = async () => {
    // Kept for header button — downloads all months as ZIP by iterating client's months
    if (!data?.consolidado?.months?.length) {
      toast.error("Sin datos consolidables");
      return;
    }
    // If only one month, download it directly
    if (data.consolidado.months.length === 1) {
      return downloadConsolidadoMonth(data.consolidado.months[0].month);
    }
    // Multiple months: sequentially download each (browser handles queue)
    setBusy("consolidado");
    try {
      for (const m of data.consolidado.months) {
        await downloadConsolidadoMonth(m.month);
        await new Promise((r) => setTimeout(r, 300));
      }
    } finally {
      setBusy(null);
    }
  };

  const downloadRda = async (r) => {
    try {
      const res = await api.get(`/rda/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar RDA");
    }
  };

  const uploadRda = async (files) => {
    if (!files?.length) return;
    setBusy("rda-upload");
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      await api.post(`/admin/clients/${userId}/rda`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("RDA subido");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
      if (rdaInputRef.current) rdaInputRef.current.value = "";
    }
  };

  const deleteRda = async (id) => {
    if (!confirm("¿Eliminar este RDA?")) return;
    try {
      await api.delete(`/admin/rda/${id}`);
      toast.success("RDA eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const uploadCedibles = async (files) => {
    if (!files?.length) return;
    setBusy("cedible-upload");
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("mes", cedibleMes);
      const { data: r } = await api.post(
        `/admin/clients/${userId}/cedible`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${r.count} cedible(s) subido(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
      if (cedibleInputRef.current) cedibleInputRef.current.value = "";
    }
  };

  const downloadCedible = async (r) => {
    try {
      const res = await api.get(`/cedible/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const deleteCedible = async (id) => {
    if (!confirm("¿Eliminar este cedible?")) return;
    try {
      await api.delete(`/admin/cedible/${id}`);
      toast.success("Cedible eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="p-10 text-slate-500">Cargando cliente...</div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell>
        <div className="p-10 text-slate-500">Cliente no encontrado.</div>
      </AppShell>
    );
  }

  const u = data.user;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <Link
          to="/admin/clientes"
          data-testid="cliente-back"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#D95A40] mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Volver a clientes
        </Link>

        <PageHeader
          eyebrow={`Cliente · ${u.id_cliente || "sin ID"}`}
          title={u.razon_social || u.name}
          description={`${u.email} · RUT ${u.rut || "—"} · Responsable: ${u.default_responsable || "—"}`}
          actions={
            <button
              onClick={downloadConsolidado}
              disabled={busy === "consolidado"}
              data-testid="cliente-download-consolidado"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              {busy === "consolidado" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileCheck className="w-4 h-4" />
              )}
              Consolidado (
              {data.consolidado.months?.length || 0} meses)
            </button>
          }
        />

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat
            icon={<Package className="w-4 h-4" />}
            label="Envíos"
            value={data.batches.length}
          />
          <Stat
            icon={<Truck className="w-4 h-4" />}
            label="RDAs"
            value={data.rdas.length}
          />
          <Stat
            icon={<FileSignature className="w-4 h-4" />}
            label="Cedibles"
            value={data.cedibles.length}
          />
          <Stat
            icon={<CheckCircle2 className="w-4 h-4" />}
            label="Cumplimiento"
            value={
              data.consolidado.total_rows
                ? `${Math.round(
                    (data.consolidado.delivered /
                      data.consolidado.total_rows) *
                      100
                  )}%`
                : "—"
            }
            highlight={data.consolidado.delivered === data.consolidado.total_rows && data.consolidado.total_rows > 0}
          />
        </div>

        {/* Section: Envíos */}
        <Section
          expanded={expanded.envios}
          onToggle={() => toggle("envios")}
          icon={<Package className="w-4 h-4" />}
          title="Envíos / Productos"
          count={data.batches.length}
          action={
            <button
              onClick={downloadClientSap}
              disabled={busy === "client-sap" || data.batches.length === 0}
              data-testid="cliente-download-sap-all"
              className="flex items-center gap-2 px-3 py-1.5 bg-[#0B1B3D] hover:bg-[#D95A40] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider"
              onClickCapture={(e) => e.stopPropagation()}
            >
              {busy === "client-sap" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-3.5 h-3.5" />
              )}
              SAP consolidado
            </button>
          }
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Fecha</Th>
                <Th>Producto</Th>
                <Th>Responsable</Th>
                <Th className="text-right">Docs</Th>
                <Th className="text-right">Unidades</Th>
                <Th>Estado</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.batches.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500">
                    Sin envíos cargados.
                  </td>
                </tr>
              )}
              {data.batches.map((b) => (
                <tr
                  key={b.id}
                  className="hover:bg-slate-50"
                  data-testid={`cliente-batch-${b.id}`}
                >
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap text-xs">
                    {new Date(b.created_at).toLocaleString("es-CL")}
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    <Link
                      to={`/batches/${b.id}`}
                      className="font-semibold text-[#0B1B3D] hover:text-[#D95A40] truncate block"
                    >
                      {b.products_summary?.[0] || b.descripcion || "—"}
                    </Link>
                    {b.products_summary?.length > 1 && (
                      <div className="text-[10px] text-slate-500">
                        +{b.products_summary.length - 1} más
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700 text-xs">
                    {b.responsable || "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">
                    {b.total_docs}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">
                    {(b.total_unidades ?? 0).toLocaleString("es-CL")}
                  </td>
                  <td className="px-3 py-2">
                    {b.downloaded_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase">
                        <CheckCircle2 className="w-3 h-3" /> Enviado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#D95A40] text-[10px] font-bold uppercase">
                        <Clock className="w-3 h-3" /> Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link
                        to={`/batches/${b.id}`}
                        className="p-1.5 hover:bg-slate-100 text-slate-600"
                        title="Ver detalle"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </Link>
                      <button
                        onClick={() => downloadBatchSap(b)}
                        disabled={busy === `batch-${b.id}`}
                        data-testid={`cliente-batch-sap-${b.id}`}
                        className="p-1.5 bg-[#0B1B3D] hover:bg-[#D95A40] disabled:opacity-50 text-white"
                        title="SAP de este envío"
                      >
                        {busy === `batch-${b.id}` ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Section: RDAs */}
        <Section
          expanded={expanded.rdas}
          onToggle={() => toggle("rdas")}
          icon={<Truck className="w-4 h-4" />}
          title="RDA — Confirmaciones de entrega"
          count={data.rdas.length}
          action={
            <>
              <input
                type="month"
                value={rdaMes}
                onChange={(e) => setRdaMes(e.target.value)}
                data-testid="cliente-rda-mes"
                className="px-2 py-1.5 border border-slate-300 text-xs focus:border-[#D95A40] focus:outline-none"
              />
              <input
                ref={rdaInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => uploadRda(e.target.files)}
                className="hidden"
                data-testid="cliente-rda-input"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  rdaInputRef.current?.click();
                }}
                disabled={busy === "rda-upload"}
                data-testid="cliente-upload-rda"
                className="flex items-center gap-2 px-3 py-1.5 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider"
              >
                {busy === "rda-upload" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                Subir RDA
              </button>
            </>
          }
        >
          <ul className="divide-y divide-slate-100">
            {data.rdas.length === 0 && (
              <li className="p-6 text-center text-slate-500 text-sm">
                Sin RDAs subidos.
              </li>
            )}
            {data.rdas.map((r) => (
              <li
                key={r.id}
                data-testid={`cliente-rda-${r.id}`}
                className="p-3 flex items-center justify-between gap-3 hover:bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0B1B3D] text-sm truncate">
                    {r.filename}
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.total_rows} filas · {r.delivered_rows} entregadas ·{" "}
                    {(r.total_bultos ?? 0).toLocaleString("es-CL")} bultos ·{" "}
                    {new Date(r.uploaded_at).toLocaleString("es-CL")}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => downloadRda(r)}
                    className="p-1.5 bg-[#0B1B3D] hover:bg-[#D95A40] text-white"
                    title="Descargar"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteRda(r.id)}
                    className="p-1.5 hover:bg-red-50 text-slate-500 hover:text-red-600"
                    title="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Section: Cedibles */}
        <Section
          expanded={expanded.cedibles}
          onToggle={() => toggle("cedibles")}
          icon={<FileSignature className="w-4 h-4" />}
          title="CEDIBLE — Guías de Despacho (PDF)"
          count={data.cedibles.length}
          action={
            <>
              <input
                type="month"
                value={cedibleMes}
                onChange={(e) => setCedibleMes(e.target.value)}
                data-testid="cliente-cedible-mes"
                className="px-2 py-1.5 border border-slate-300 text-xs focus:border-[#D95A40] focus:outline-none"
              />
              <input
                ref={cedibleInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => uploadCedibles(e.target.files)}
                className="hidden"
                data-testid="cliente-cedible-input"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cedibleInputRef.current?.click();
                }}
                disabled={busy === "cedible-upload"}
                data-testid="cliente-upload-cedible"
                className="flex items-center gap-2 px-3 py-1.5 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider"
              >
                {busy === "cedible-upload" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                Subir PDFs
              </button>
            </>
          }
        >
          <ul className="divide-y divide-slate-100">
            {data.cedibles.length === 0 && (
              <li className="p-6 text-center text-slate-500 text-sm">
                Sin cedibles subidos.
              </li>
            )}
            {data.cedibles.map((c) => (
              <li
                key={c.id}
                data-testid={`cliente-cedible-${c.id}`}
                className="p-3 flex items-center justify-between gap-3 hover:bg-slate-50"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-red-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B1B3D] text-sm truncate">
                      {c.filename}
                    </div>
                    <div className="text-xs text-slate-500">
                      {c.folio ? (
                        <>
                          Folio GD{" "}
                          <span className="text-[#D95A40] font-bold">
                            {c.folio}
                          </span>{" "}
                          ·{" "}
                        </>
                      ) : (
                        ""
                      )}
                      {((c.size_bytes || 0) / 1024).toFixed(1)} KB ·{" "}
                      {new Date(c.uploaded_at).toLocaleString("es-CL")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => downloadCedible(c)}
                    className="p-1.5 bg-[#0B1B3D] hover:bg-[#D95A40] text-white"
                    title="Descargar"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteCedible(c.id)}
                    className="p-1.5 hover:bg-red-50 text-slate-500 hover:text-red-600"
                    title="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Section: Consolidado */}
        <Section
          expanded={expanded.consolidado}
          onToggle={() => toggle("consolidado")}
          icon={<FileCheck className="w-4 h-4" />}
          title="Consolidado SAP + RDA (por mes)"
          count={data.consolidado.total_rows}
        >
          <div className="p-5">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <MiniStat
                label="Filas totales"
                value={data.consolidado.total_rows}
              />
              <MiniStat
                label="Entregadas"
                value={data.consolidado.delivered}
                good
              />
              <MiniStat
                label="Pendientes"
                value={data.consolidado.pending}
                warn={data.consolidado.pending > 0}
              />
            </div>
            {(data.consolidado.months || []).length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6 border border-slate-200 bg-slate-50">
                Sin envíos registrados. No hay consolidado disponible.
              </div>
            ) : (
              <div className="grid gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">
                  Archivos por mes
                </div>
                {(data.consolidado.months || []).map((m) => (
                  <div
                    key={m.month}
                    data-testid={`cliente-consolidado-month-${m.month}`}
                    className="bg-white border border-slate-200 p-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-[#D95A40]/10 text-[#D95A40] flex items-center justify-center shrink-0">
                        <FileCheck className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-[#0B1B3D]">
                          {m.label}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {m.batches} envíos · {m.total_rows} filas ·{" "}
                          <span className="text-emerald-700 font-semibold">
                            {m.delivered} entregadas
                          </span>{" "}
                          ·{" "}
                          <span className="text-[#D95A40]">
                            {m.pending} pendientes
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadConsolidadoMonth(m.month)}
                      disabled={busy === `consolidado-${m.month}`}
                      data-testid={`cliente-consolidado-download-${m.month}`}
                      className="flex items-center gap-2 px-3 py-2 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider shrink-0"
                    >
                      {busy === `consolidado-${m.month}` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Descargar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>
      </div>
    </AppShell>
  );
}

function Section({ expanded, onToggle, icon, title, count, action, children }) {
  return (
    <div className="bg-white border border-slate-200 mb-4">
      <div
        onClick={onToggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggle()}
        role="button"
        tabIndex={0}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left cursor-pointer"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          <div className="w-8 h-8 bg-[#0B1B3D] text-white flex items-center justify-center">
            {icon}
          </div>
          <div>
            <div className="font-display font-bold text-[#0B1B3D]">{title}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
              {count} elementos
            </div>
          </div>
        </div>
        {action && (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
            {action}
          </div>
        )}
      </div>
      {expanded && (
        <div className="border-t border-slate-100">{children}</div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, highlight }) {
  return (
    <div
      className={`border p-4 ${
        highlight
          ? "border-emerald-300 bg-emerald-50/40"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className={highlight ? "text-emerald-600" : "text-[#D95A40]"}>
          {icon}
        </div>
      </div>
      <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function MiniStat({ label, value, good, warn }) {
  const color = good ? "text-emerald-700" : warn ? "text-[#D95A40]" : "text-[#0B1B3D]";
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
        {label}
      </div>
      <div className={`font-display font-black text-2xl tabular-nums ${color}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 ${className}`}
    >
      {children}
    </th>
  );
}
